import { FormsModule } from '@angular/forms';
import { from as observableFrom, Observable } from 'rxjs';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ContactsUpgradeService } from '../../Contact/contacts.service.upgrade';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { ExtendedModelBase } from '../../Compliance/Models/extendedModelBase';
import { TeamService } from '../../Team/team.service';

import { map, mergeMap } from 'rxjs/operators';
import { WsTypeaheadDirective } from '../../UI-Lib/Typeahead/typeahead.directive';

export class ExtendedContactModelCore extends ExtendedModelBase<Core.ContactModel> {
    constructor(model: any) {
        super(model);
        this.includeCompany = model.includeCompany;
        this.displayName = model.displayName;
    }

    includeCompany: boolean;
    displayName: string;
}

@Component({
    selector: 'user-picker',
    template: `
        <input
            (blur)="onContactBlur()"
            (typeaheadOnSelect)="onContactSelected($event)"
            [(ngModel)]="contactFilter"
            [disabled]="isReadonly"
            [typeaheadMinLength]="2"
            [typeaheadOptionsLimit]="100"
            [typeaheadScrollable]="true"
            [typeaheadWaitMs]="750"
            [wsTypeahead]="contacts$"
            autocomplete="off"
            [class]="inputClass ? 'form-control ' + inputClass : 'form-control'"
            container="body"
            name="contact"
            placeholder="Filter contacts..."
            type="text"
            typeaheadOptionField="displayName"
        />
    `,
    styles: [
        `
            .overridden {
                color: var(--ace-danger) !important;
            }

            .differs-from-system {
                color: blue;
            }
        `,
    ],
    imports: [WsTypeaheadDirective, FormsModule],
})
export class UserPickerComponent implements OnChanges, OnInit {
    constructor(
        private _contactService: ContactsUpgradeService,
        private readonly _teamService: TeamService,
    ) {}

    @Input() currentContact: Core.ContactModel;
    @Input() isReadonly: boolean;
    @Input() currentContactId: number;
    @Input() includeBlank: boolean;
    @Input() search: any;
    @Input() includeCompany: boolean = false;
    @Input() usersOnly: boolean = false;
    @Input() searchAllAssignable: boolean;
    @Input() currentContactDisplay: string;
    // Allows revertable fields to color the actual text input, not just the wrapping component host.
    @Input() inputClass: string;

    @Output() contactSelected: EventEmitter<Core.ContactModel> = new EventEmitter<Core.ContactModel>();
    @Output() contactChanged: EventEmitter<ExtendedContactModelCore> = new EventEmitter<ExtendedContactModelCore>();

    contactFilter: string = '';
    contacts$: Observable<ExtendedContactModelCore[]> = (
        Observable.create((observer: any) => {
            observer.next(this.contactFilter);
        }) as Observable<string>
    ).pipe(mergeMap((token) => this._filterContacts(token)));

    private _hasChanges: boolean;
    private _contactNoResult: boolean = false;
    private _lastAppliedContactFilter: string;

    async ngOnInit(): Promise<void> {
        if (
            this.currentContactId &&
            (!this.currentContact || (!this.currentContact.lastName && !this.currentContact.firstName))
        ) {
            this.currentContact = await this._contactService.getById(this.currentContactId);
        }

        if (this.searchAllAssignable) {
            const allAssignable = await this._teamService.getAllAssignableUsers(false);
            this.search = {
                find: (filter: string) => {
                    return Promise.resolve(
                        Array.from(
                            new Map(
                                allAssignable
                                    .filter(
                                        (user) =>
                                            user.lastName?.toLowerCase().includes(filter.toLowerCase()) ||
                                            user.firstName?.toLowerCase().includes(filter.toLowerCase()),
                                    )
                                    .map((user) => [user.userID, user]),
                            ).values(),
                        ),
                    );
                },
            };
        }

        this._setContactFilterToCurrentValue();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.currentContact || changes.currentContactDisplay || changes.currentContactId) {
            const currentContactFilter = this._getContactFilterForCurrentValue();
            // Parent components may rebuild currentContact during change detection; only reset the filter when
            // the selected display value really changed so typeahead input is not overwritten while typing.
            if (currentContactFilter !== this._lastAppliedContactFilter) {
                this._setContactFilterToCurrentValue();
            }
        }
    }

    get contact(): Core.ContactModel {
        return this.currentContact;
    }

    set contact(value: Core.ContactModel) {
        const contactId: number = value ? value.contactID : null;

        if (this.currentContactId !== contactId) {
            let nameStr = `${value.lastName}, ${value.firstName}`;
            if (this.includeCompany) {
                nameStr += ` (${(value as any).entityName})`;
            }
            const extendedContact = new ExtendedContactModelCore({
                ...value,
                ...{
                    displayName: nameStr,
                    includeCompany: this.includeCompany,
                },
            } as Core.ContactModel);

            this.contactChanged.emit(extendedContact);
        }

        this._hasChanges = true;
        this.currentContact = value;
        this.currentContactId = contactId;
        this.contactSelected.emit(value);
    }

    get hasChanges(): boolean {
        return this._hasChanges;
    }

    onContactSelected(match: TypeaheadMatch): void {
        this.contact = (match.item as ExtendedContactModelCore).model;
        this.contactFilter = match.value;
        this._hasChanges = true;
    }

    onContactBlur(): void {
        if (this.contactFilter.trim() === '' || this._contactNoResult) {
            this._setContactFilterToCurrentValue();
        }
    }

    get contactDisplay(): string {
        if (this.currentContactDisplay) {
            return this.currentContactDisplay;
        }

        const contact = this.currentContact;

        return contact ? `${contact.lastName}, ${contact.firstName}` : '';
    }

    private _filterContacts(filter: string): Observable<ExtendedContactModelCore[]> {
        const result = <Array<any>>this.search.find(filter);
        return observableFrom(result).pipe(
            map((contacts) => {
                if (this.usersOnly) {
                    contacts = contacts.filter((x) => x.userID);
                }

                if (contacts && contacts.length === 0) {
                    this._contactNoResult = true;
                }

                if (this.includeBlank) {
                    contacts.unshift(<Core.ContactModel>{ contactID: -1, firstName: '[Blank]', lastName: '' });
                }

                return contacts.map((contact) => {
                    let nameStr = `${contact.lastName}, ${contact.firstName}`;
                    if (this.includeCompany) {
                        nameStr += ` (${(contact as any).entityName})`;
                    }
                    return new ExtendedContactModelCore({
                        ...contact,
                        ...{
                            displayName: nameStr,
                            includeCompany: this.includeCompany,
                        },
                    } as Core.ContactModel);
                });
            }),
        );
    }

    private _setContactFilterToCurrentValue(): void {
        this.contactFilter = this._getContactFilterForCurrentValue();
        this._lastAppliedContactFilter = this.contactFilter;
    }

    private _getContactFilterForCurrentValue(): string {
        return (this.currentContact && this.contactDisplay) || '';
    }
}
